<template>
  <v-stepper v-model="purchaseWizardStep" vertical class="elevation-0">
    <!-- Step 1 -->
    <v-stepper-step :complete="purchaseWizardStep > 1" :step="1">
      {{ $tt('SelectPlanDialogTitle') }}
    </v-stepper-step>
    <v-stepper-content :step="1">
      <v-card>
        <v-card-title class="py-0 my-0">
          <v-list-item>
            <v-list-item-content>
              <v-list-item-title>
                <h3 v-if="isStandardMode()">{{ $tt('StandardPlanDisplayName') }}</h3>
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-card-title>
        <v-card-text class="d-md-flex">
          <!-- プラン一覧 -->
          <v-card v-for="(plan, i) in billingPlans" min-width="300" class="ma-1" :key="i">
            <v-card-text v-if="isYearlyPlan(plan)" style="min-height: 140px;">
              <div class="subtitle-1">
                {{ $tt('SelectPlanDialogYearPlan') }}
                <v-chip color="warning" small>{{ $tt('SelectPlanDialogRecommend') }}</v-chip>
              </div>
              <div>
                <span class="display-1">{{ getPlanAmount(plan) | aidFormatPrice(plan.currency) }}</span>
                {{ $tt('StandardPlanPricePerYear') }}
              </div>
            </v-card-text>
            <v-card-text v-if="!isYearlyPlan(plan)" style="min-height: 140px;">
              <div class="subtitle-1" v-if="!isEnterpriseMode()">{{ $tt('SelectPlanDialogYearMonth') }}</div>
              <div class="subtitle-1" v-else>{{ $tt('customEnterprisePlanDisplayName') }}</div>
              <div>
                <span class="display-1">{{ getPlanAmount(plan) | aidFormatPrice(plan.currency) }}</span>
                {{ $tt('StandardPlanPricePerMonth') }}
              </div>
              <div v-if="!isEnterpriseMode()">
                {{ $tt('StandardPlanPriceForMonthBatch') }}
                {{ (getPlanAmount(plan) * 12) | aidFormatPrice(plan.currency) }}
              </div>
              <div v-else>※ {{ $tt('customPricePerMember') }}</div>
            </v-card-text>
            <v-card-actions>
              <v-btn
                v-if="!isEnterpriseMode()"
                :color="isYearlyPlan(plan) ? 'warning' : 'primary'"
                block
                @click="selectBillingPlan(plan)"
                rounded
                class="mb-3"
              >
                {{ getPlanSelectButtonText(plan) }}
              </v-btn>
              <v-btn
                v-else
                :color="isYearlyPlan(plan) ? 'warning' : 'primary'"
                block
                @click="selectBillingPlan(plan)"
                rounded
                class="mb-3"
              >
                {{ $tt('customSelectThis') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-card-text>
        <!-- 表示期間の告知 (案C: subscription-data-access-control.md) -->
        <v-card-text class="caption pt-0">
          {{ $tt('selectPlanDataViewNote') }}
        </v-card-text>
        <v-card-actions>
          <v-btn @click="closePurchaseWizardDialog" outlined text>
            {{ $tt('customCancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-stepper-content>
    <!-- Step 2 -->
    <v-stepper-step :complete="purchaseWizardStep > 2" :step="2">
      {{ $tt('customSelectPlanSelectPaymentMethodText') }}
    </v-stepper-step>
    <v-stepper-content :step="2">
      <v-card>
        <v-card-title class="py-0 my-0">
          <v-list-item>
            <v-list-item-content>
              <v-list-item-title>{{ $tt('customSelectPlanSelectPaymentMethodText') }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-card-title>
        <v-card-text class="d-md-flex pb-0">
          <v-select
            v-model="selectedPaymentMethodType"
            :items="stripePaymentMethodTypes"
            item-text="name"
            item-value="value"
            return-object
            :label="$tt('customBillingMethod')"
            class="col-6"
            hide-details
          />
        </v-card-text>
        <v-card-text class="d-md-flex" v-if="isCardPayment()">
          <v-container>
            <v-row justify="center">
              <!-- 新しいカードの追加 同カードも追加できるので入力だけにする -->
              <v-col md="12" cols="12">
                <add-credit-card-component @setStripeAddCardRequest="setStripeAddCardRequest($event)" />
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions class="mt-2">
          <v-btn @click="selectPaymentMethodType()" color="primary" v-if="!isCardPayment()">
            {{ $tt('customSelectThis') }}
          </v-btn>
          <v-btn @click="backStep()">
            {{ $tt('customBack') }}
          </v-btn>
          <v-spacer />
          <v-btn @click="closePurchaseWizardDialog" outlined text>
            {{ $tt('customCancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-stepper-content>
    <v-stepper-step :complete="purchaseWizardStep > 3" :step="3">
      {{ $tt('customBillingMethodConfirm') }}
    </v-stepper-step>
    <v-stepper-content :step="3">
      <v-card v-if="selectedBillingPlan">
        <v-card-text class="">
          <v-sheet elevation="3" class="pa-4" color="grey lighten-3">
            <dl class="row ma-0 pa-0">
              <dt class="col-sm-2 font-weight-bold">
                {{ $tt('customBillingPlan') }}
              </dt>
              <dd class="col-sm-9">
                {{ getPlanName(selectedBillingPlan) }}
              </dd>
              <dt class="col-sm-2 font-weight-bold">
                {{ $tt('customPriceIncludingTax') }}
              </dt>
              <dd class="col-sm-9">
                {{ getPlanAmount(selectedBillingPlan) | aidFormatPrice(selectedBillingPlan.currency) }}
                <span v-if="hasCoupon" class="red--text"
                  >- {{ couponData.amountOff | aidFormatPrice(selectedBillingPlan.currency) }} =
                  {{ getTotalAmount() | aidFormatPrice(selectedBillingPlan.currency) }}</span
                >
              </dd>
              <dt class="col-sm-2 font-weight-bold">
                {{ $tt('customBillingMethod') }}
              </dt>
              <dd class="col-sm-9">
                <div>
                  {{ getPaymentMethodType() }}
                </div>
              </dd>
            </dl>
          </v-sheet>
        </v-card-text>
        <v-card-actions>
          <v-btn @click="openInputCoupon()" v-if="isShowCouponBtn && !isShowInputCoupon" color="blue" text>
            <v-icon left>
              mdi-plus
            </v-icon>
            {{ $tt('customStripeCouponText1') }}
          </v-btn>
          <v-text-field
            v-model="couponCode"
            v-if="isShowCouponBtn && isShowInputCoupon"
            :label="$tt('customStripeCouponText2')"
          ></v-text-field>
          <v-spacer />
          <v-btn @click="closePurchaseWizardDialog()" text>
            {{ $tt('customCancel') }}
          </v-btn>
          <v-btn @click="backStep()">
            {{ $tt('customBack') }}
          </v-btn>
          <v-btn @click="subscribe()" color="warning" :disabled="selectedPaymentMethodType === null">
            <span v-if="isStandardMode()">{{ $tt('customPay') }}</span>
            <span v-else>{{ $tt('customEnterprisePlanStart') }}</span>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-stepper-content>
  </v-stepper>
</template>

<script>
import AddCreditCardComponent from '@/components/billing/AddCreditCardComponent'
import StripeInfo from '@/lib/stripe-info'
import Currency from '@/lib/currency'
import StripePaymentMethodTypes from '@/lib/stripe-payment-method-types'
import UserInfo from '@/lib/user-info'
import i18n from '@/lib/i18n'
import * as Constants from '@/lib/constants'
import BillingMixin from '@/components/mixins/billing-mixin'
import UTCDateTime from '@/lib/utc-datetime'
import moment from 'moment'
import Toast from '@/lib/toast'

export default {
  name: 'SelectPlanComponent',
  components: { AddCreditCardComponent },
  mixins: [BillingMixin],
  props: ['plan'],
  data() {
    return {
      purchaseWizardStep: 1,
      currencyItems: Currency.getCurrencyItems(),
      stripePaymentMethodTypes: StripePaymentMethodTypes.getStripePaymentMethodTypes(),
      isShowCouponBtn: false,
      isShowInputCoupon: false,
      addCardRequest: null,
      hasCoupon: false,
      couponData: null,
    }
  },
  mounted() {
    this.planMode = this.plan
    if (this.plan === Constants.PLUS_PLAN) {
      this.stripePaymentMethodTypes = StripePaymentMethodTypes.getStripeCardPaymentMethodTypeOnly()
    } else {
      this.stripePaymentMethodTypes = StripePaymentMethodTypes.getStripePaymentMethodTypes()
    }
    if (this.stripeCurrency) {
      this.initBillingPlans(this.plan)
    } else {
      this.applyLanguageCurrency()
    }
    this.$store.dispatch('listStripeCoupons')
  },
  methods: {
    openInputCoupon() {
      this.isShowInputCoupon = true
    },
    isStandardMode() {
      return this.plan === Constants.STANDARD_PLAN
    },
    // 支払い通貨は選択式にせず、設定言語で自動決定する (日本語=円 / その他=ドル)
    applyLanguageCurrency() {
      const currency = UserInfo.getLanguage() === 'ja' ? Currency.JPY : Currency.USD
      this.selectedCurrencyItem = Currency.getItemByCurrency(currency)
    },
    isEnterpriseMode() {
      return this.plan === Constants.ENTERPRISE_PLAN
    },
    closePurchaseWizardDialog() {
      this.$emit('closePurchaseWizardDialog')
    },
    closeNowLoading() {
      this.$emit('closeNowLoading')
    },
    openNowLoading() {
      this.$emit('openNowLoading')
    },
    subscribe() {
      if (this.isShowCouponBtn) {
        this.$emit('inputCouponCode', this.couponCode)
      }
      this.$emit('subscribe')
    },
    isYearlyPlan(plan) {
      return plan.interval === 'year'
    },
    getPlanAmount(plan) {
      if (plan.currency === Currency.JPY) {
        return plan.amount
      } else {
        return plan.amount * 0.01
      }
    },
    getPlanSelectButtonText(plan) {
      if (!UserInfo.isTrialAvailable()) {
        return i18n.tt('customSelectThis')
      }
      if (this.isYearlyPlan(plan)) {
        return i18n.tt('SelectPlanSubscribeYear')
      } else {
        return i18n.tt('SelectPlanSubscribeMonth')
      }
    },
    selectBillingPlan(plan) {
      this.selectedBillingPlan = plan

      this.$emit('selectBillingPlan', plan)

      this.purchaseWizardStep = 2
    },
    selectPaymentMethodType() {
      if (this.selectedPaymentMethodType) {
        if (this.isCardPayment()) {
          if (!this.addCardRequest) {
            Toast.i18nError('customCardInfoHasNotBeenEnteredError')
            return
          }
        }
        this.$emit('selectPaymentMethodType', this.selectedPaymentMethodType)
        this.isShowCouponBtn = true
        this.purchaseWizardStep = 3
      } else {
        Toast.i18nError('customNoPaymentMethodSeletedError')
      }
    },
    setStripeAddCardRequest(stripeAddCardRequest) {
      this.addCardRequest = stripeAddCardRequest
      this.$emit('setStripeAddCardRequest', this.addCardRequest)
      this.selectPaymentMethodType()
    },
    getPlanName(plan) {
      // API の displayName (AgriBus Professional 等) を表示。plan は識別子なので表示には使わない
      return plan.displayName || plan.plan
    },
    getPaymentMethodType() {
      if (this.selectedPaymentMethodType) {
        return this.selectedPaymentMethodType.name
      }
      return ''
    },
    initialPaymentDate() {
      let now = moment()
      now.add(1, 'months')
      return UTCDateTime.utcStringFromMomentObject(now)
    },
    isCardPayment() {
      if (this.selectedPaymentMethodType) {
        return this.selectedPaymentMethodType.value === StripePaymentMethodTypes.Card
      }
      return false
    },
    getCards() {
      return StripeInfo.getCards()
    },
    backStep() {
      this.purchaseWizardStep = this.purchaseWizardStep - 1
    },
    getTotalAmount() {
      const planAmount = this.getPlanAmount(this.selectedBillingPlan)
      const couponAmount = this.couponData.amountOff
      return planAmount - couponAmount
    },
  },
  watch: {
    plan: function(val) {
      this.selectedBillingPlan = null
      this.selectedCurrencyItem = null
      this.purchaseWizardStep = 1
      if (val === Constants.PLUS_PLAN) {
        this.stripePaymentMethodTypes = StripePaymentMethodTypes.getStripeCardPaymentMethodTypeOnly()
      } else {
        this.stripePaymentMethodTypes = StripePaymentMethodTypes.getStripePaymentMethodTypes()
      }
      this.billingPlans = []
      if (this.stripeCurrency) {
        this.initBillingPlans(this.plan)
      } else {
        this.applyLanguageCurrency()
      }
    },
    stripeCurrency: function(val) {
      this.selectedBillingPlan = null
      this.initBillingPlans(this.planMode)
    },
    couponCode(val) {
      const coupons = this.$store.getters.coupons
      const coupon = coupons.find(coupon => coupon.id === val)
      if (typeof coupon !== 'undefined') {
        this.hasCoupon = true
        this.couponData = coupon
      } else {
        this.hasCoupon = false
        this.couponData = null
      }
    },
  },
}
</script>
